import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge, Button, Progress, StatusBadge, type StatusBadgeProps } from "@h2j-data-team/ui";
import type { QvdAnalysisRun } from "@repo/shared";

import { useFormatDate } from "@/hooks/use-format-date";
import { isRunActive } from "@/features/qvd-analysis/api";

const TERMINAL_TONE: Record<string, NonNullable<StatusBadgeProps["tone"]>> = {
  completed: "success",
  failed: "danger",
  cancelled: "neutral",
};

export function QvdAnalysisStatusStrip({
  run,
  onCancel,
  cancelPending,
}: {
  run: QvdAnalysisRun;
  onCancel: () => void;
  cancelPending: boolean;
}) {
  const { t } = useTranslation("qvd-analysis");
  const { formatDateTime } = useFormatDate();

  // A space-scoped run has no per-QVD filter (and vice-versa) — show only the
  // populated part instead of a misleading "· 0 QVDs".
  const spaceCount = run.scope.spaceIds.length;
  const qvdCount = run.scope.qvdIds.length;
  const scopeChip =
    spaceCount === 0 && qvdCount === 0
      ? t("scope.wholeTenant")
      : qvdCount === 0
        ? t("scope.spacesCount", { count: spaceCount })
        : spaceCount === 0
          ? t("scope.qvdsCount", { count: qvdCount })
          : t("scope.selected", { spaces: spaceCount, qvds: qvdCount });

  if (isRunActive(run.status)) {
    const p = run.progress;
    const pct = p && p.total > 0 ? Math.round((p.processed / p.total) * 100) : 0;
    // The inventory phase counts spaces scanned (not QVDs) and reports the
    // running file tally, so it needs its own labels; the lineage phases keep
    // the QVD-based progress + coverage readout.
    const isInventory = run.phase === "inventory";
    // The orphan phase samples QVD families across three stages, so its progress
    // denominator swings (sampled subset → app pool → full inventory) and never
    // matches the space's file count. Showing "X de Y QVDs" there reads as partial
    // coverage, so we drop the ratio and keep only the bar + orphan/coverage tally.
    const isOrphans = run.phase === "orphans";
    const isConsumption = run.phase === "consumption";
    const phase = t(`status.phase.${run.phase}`);
    const progressText =
      p && p.total > 0 && !isOrphans
        ? isInventory
          ? t("status.progressSpaces", { processed: p.processed, total: p.total })
          : isConsumption
            ? t("status.progressApps", { processed: p.processed, total: p.total })
            : t("status.progress", { processed: p.processed, total: p.total })
        : null;
    return (
      <div className="flex flex-col gap-2.5 rounded-lg border border-hairline bg-surface px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <Loader2 aria-hidden className="size-4 shrink-0 animate-spin text-brand" />
            {/* The phase changes rarely, so it is announced; the progress below
                changes every couple of seconds and is left to the bar. */}
            <span role="status" className="font-semibold text-ink">
              {phase}
            </span>
            {progressText && <span className="text-dim">· {progressText}</span>}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={cancelPending || run.status === "cancelling"}
          >
            {run.status === "cancelling" ? t("status.cancelling") : t("status.cancel")}
          </Button>
        </div>
        <Progress value={pct} aria-label={phase} aria-valuetext={progressText ?? `${pct}%`} />
        {isInventory
          ? p && (
              <div className="text-xs text-dim">
                {t("status.filesFound", { files: (p.found ?? 0).toLocaleString() })}
              </div>
            )
          : run.coverage && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-dim">
                  {t("status.partialOrphans", { count: run.coverage.orphanCount })}
                </span>
                <span className="font-mono text-faint">
                  {t("status.notAnalyzable", { count: run.coverage.qvdsNotAnalyzable })}
                </span>
              </div>
            )}
      </div>
    );
  }

  const tone = TERMINAL_TONE[run.status] ?? "neutral";
  const finishedAt = run.finishedAt ? formatDateTime(run.finishedAt) : null;

  return (
    <div className="flex items-center justify-between gap-3 rounded-lg bg-surface-muted px-4 py-3">
      <div className="flex items-center gap-2.5">
        <StatusBadge tone={tone}>{t(`status.state.${run.status}`)}</StatusBadge>
        {finishedAt && <span className="text-dim">· {finishedAt}</span>}
      </div>
      <div className="flex items-center gap-3">
        {run.coverage && run.coverage.qvdsFromCache > 0 && (
          <span className="text-xs text-faint">
            {t("status.fromCache", { count: run.coverage.qvdsFromCache })}
          </span>
        )}
        <Badge variant="outline" className="font-mono">
          {scopeChip}
        </Badge>
      </div>
    </div>
  );
}
